import React from 'react';
import I18N from '@leverege/i18n';
import { CreateAction, DeleteAction, RelationshipBreadcrumbs, UpdateAction } from '@leverege/molten/imagine';
import { LinkAction } from '@leverege/molten/routes';
import { GroupScreen } from '@leverege/molten/screens';
import { Attribute } from '@leverege/api-attributes';
import { v4 } from '@leverege/base62-util';
import { GlobalState, UI } from '@leverege/ui-redux';
import { Relationships } from '@leverege/ui-attributes';
import ImagineRsrcGroupDelegate from './ImagineRsrcGroupDelegate.js';
import RsrcRelationship from './RsrcRelationship.js';
import RsrcBlueprints from './RsrcBlueprints.js';
const {
  tf,
  tfIcon
} = I18N.ns('molten.imagine.RsrcAccess');
function createAttribute(blueprint, rsrcBlueprint) {
  const attr = {
    blueprintId: blueprint.id,
    childPath: 'rsrc',
    desc: `Resources for this ${blueprint.name}`,
    id: v4(),
    isCollection: false,
    name: 'Resources',
    owned: false,
    path: 'rsrcs',
    relationshipType: 'oneToMany',
    source: 'user',
    targetBlueprintId: rsrcBlueprint.id,
    type: 'relationship',
    metadata: {
      icons: {
        screenIcon: 'fa-solid fa-grid-2'
      }
    }
  };
  return new Attribute(blueprint, attr);
}

/**
 * Processes the relationship for user management delegate. If the relationship blueprint has
 * the `has:userManagement` tag, a `People` link will be added to the nav bar leading to the 
 * resource users screen.
 */
function processRelationship(molten, relationship, options) {
  // Get parameters
  const {
    blueprint,
    path,
    attribute
  } = relationship;

  // Check if blueprint has user management
  // if ( !blueprint.hasTag( 'has:userManagement' ) ) {
  //   return
  // }

  // Initialize parameters
  const actionPath = `${relationship.urlPath}/${relationship.isGroup ? `:${blueprint.type}Id/` : ''}rsrc`;
  const excludeFrom = blueprint.getTagsStartingWith('excludeRsrcManagementFrom:', true);
  const attrExcludeFrom = attribute ? attribute.getTagsStartingWith('excludeRsrcManagementFrom:', true) : [];
  const navSortRsrcs = blueprint.getTagStartingWith('navSortRsrcManagement:', true) || 'zzz';
  const sectionName = blueprint.getString('rsrcManagementSectionName');
  const rsrcBlueprint = RsrcBlueprints.getByType('rsrc');
  const rsrcAttribute = createAttribute(blueprint, rsrcBlueprint);

  // Add link
  molten.addPlugin('Action', LinkAction.create({
    id: `link.${path}.Rsrcs`,
    matches: {
      client: 'ItemScreen',
      objectType: blueprint.type,
      path,
      use: 'navBar'
    },
    name: tf('name'),
    icon: tfIcon('icon'),
    path: actionPath,
    layout: {
      sort: navSortRsrcs,
      // put at end
      sectionName
    },
    handles: cxt => {
      const aPersona = UI.get(GlobalState.get()).activePersona?.name;
      return !(attribute == null && (excludeFrom.includes(aPersona) || excludeFrom.includes('*')) || attrExcludeFrom.includes(aPersona) || attrExcludeFrom.includes('*'));
    }
  }));
  const subpath = 'rsrc';
  const parentRel = relationship;
  const rsrcRel = new RsrcRelationship({
    refPath: `${parentRel.refPath}/:${parentRel.options.idKey}/${subpath}`,
    urlPath: `${parentRel.urlPath}/:${parentRel.options.idKey}/${subpath}`,
    apiName: 'imagineRsrc',
    path: `${parentRel.path}.${subpath}`,
    subpath,
    parent: relationship,
    blueprint: rsrcBlueprint,
    attribute: rsrcAttribute,
    systemId: parentRel.systemId,
    name: 'Resources'
  });
  Relationships.add(rsrcRel);

  // Add Breadcrumbs
  molten.addPlugin('Route', {
    id: `route.${path}.ResourceRsrcs:breadcrumbs`,
    matches: {
      client: 'Breadcrumbs'
    },
    exact: true,
    path: actionPath,
    render: ({
      match,
      ...rest
    }) => {
      const parent = parentRel.createItem(match);
      const rel = rsrcRel.createResolved(match, {
        parent
      });
      return /*#__PURE__*/React.createElement(RelationshipBreadcrumbs, {
        match,
        ...rest,
        relationship: rel
      });
    }
  });
  molten.addPlugin('Action', {
    matches: [{
      use: ['contextMenu', 'mobileActionBar'],
      path: rsrcRel.path,
      objectType: rsrcRel.objectType
    }, {
      client: ['MapBoxRolloverSelectionPointer', 'ListDataViewer'],
      use: ['contextMenu', 'mobileActionBar']
    }],
    ...DeleteAction.create(rsrcRel)
  });
  molten.addPlugin('Action', {
    matches: {
      client: ['GroupScreen', 'TableDataViewer'],
      use: ['contextMenu', 'mobileActionBar', 'noData'],
      path: rsrcRel.path,
      objectType: rsrcRel.objectType
    },
    ...CreateAction.create(rsrcRel)
  });
  molten.addPlugin('Action', {
    matches: [{
      // client : [ null, 'GroupScreen' ],
      use: ['contextMenu', 'mobileActionBar'],
      path: rsrcRel.path,
      objectType: rsrcRel.objectType
    }, {
      client: ['GoogleMapsRolloverSelectionPointer', 'MapBoxRolloverSelectionPointer', 'ListDataViewer'],
      use: ['contextMenu', 'mobileActionBar']
    }],
    ...UpdateAction.create(rsrcRel)
  });

  // Add route
  molten.addPlugin('Route', {
    id: `route.${path}.ResourceRsrcs`,
    exact: true,
    path: actionPath,
    matches: {
      client: 'ItemScreen',
      path,
      objectType: blueprint.type
    },
    render: ({
      match,
      ...rest
    }) => {
      const parent = parentRel.createItem(match);
      const iRel = rsrcRel.createResolved(match, {
        parent
      });
      const delegate = new ImagineRsrcGroupDelegate({
        match,
        relationship: iRel,
        queryName: `${relationship.path}.rsrc.search`
      }, match.url);

      // const [ path, setPath ] = useState()
      const props = {
        ...rest,
        match,
        // path,
        // onPathChange : setPath,
        delegate,
        targetKey: `RsrcScreen.${path}`,
        selectionKey: 'selected',
        rolloverKey: 'rollover',
        objectType: 'rsrc',
        relationship: iRel
      };
      return /*#__PURE__*/React.createElement(GroupScreen, props);
    }
  });
}
export default {
  id: 'rsrc.RsrcAccessCreator',
  processRelationship
};